/**
 * Effective OG image + content-image inventory — shared resolver.
 *
 * Mirrors backend/shared/og_image.py 1:1. If you change the logic here,
 * change it there too (a parity test asserts the two scorers agree).
 *
 *  1. resolveEffectiveOgImage(doc, contentType) — which image is the Open
 *     Graph / social-share image? A manual staff-selected `og_image_url`
 *     ALWAYS wins; only when blank do we derive one live from the primary
 *     content image. Nothing is persisted (no `is_auto` field, no
 *     migration), so swapping the primary image swaps the effective OG
 *     image, while a manual pick is never overwritten.
 *
 *  2. collectContentImages(doc, contentType) — every legitimate content
 *     image paired with its own alt metadata, so the SEO scorer can judge
 *     image EXISTENCE separately from image METADATA.
 */

const IMG_TAG_RE = /<img\b[^>]*>/gi;
const ALT_ATTR_RE = /\balt\s*=\s*"([^"]*)"/i;
const SRC_ATTR_RE = /\bsrc\s*=\s*"([^"]*)"/i;

const bodyHtml = (doc) => doc.html_content || doc.content || '';

// Listing media, images only, in display order.
const mediaImages = (doc) => {
  const media = doc.media;
  if (!Array.isArray(media)) return [];
  return media
    .filter((m) => m && typeof m === 'object' && (m.kind || 'image') === 'image')
    .slice()
    .sort((a, b) => (a.order || 0) - (b.order || 0));
};

const bodyImages = (html) => {
  const tags = (html || '').match(IMG_TAG_RE) || [];
  return tags.map((tag) => {
    const altM = tag.match(ALT_ATTR_RE);
    const srcM = tag.match(SRC_ATTR_RE);
    return {
      url: srcM ? srcM[1].trim() : '',
      alt: altM ? altM[1].trim() : '',
    };
  });
};

/**
 * All content images as [{url, alt}], primary image first. Per content type:
 *   blog     -> cover_image_url (alt: cover_image_alt)
 *   page     -> hero_image_url  (alt: hero_image_alt)   [location pages]
 *   listing  -> media[] images  (alt: alt_text)
 *   category -> none
 * Body <img> tags are appended for every content type.
 */
export const collectContentImages = (doc = {}, contentType = 'page') => {
  const images = [];

  if (contentType === 'blog') {
    const url = (doc.cover_image_url || '').trim();
    if (url) images.push({ url, alt: (doc.cover_image_alt || '').trim() });
  } else if (contentType === 'page') {
    const url = (doc.hero_image_url || '').trim();
    if (url) images.push({ url, alt: (doc.hero_image_alt || '').trim() });
  } else if (contentType === 'listing') {
    for (const m of mediaImages(doc)) {
      const url = (m.public_url || '').trim();
      if (url) images.push({ url, alt: (m.alt_text || '').trim() });
    }
  }

  return images.concat(bodyImages(bodyHtml(doc)));
};

/**
 * Manual OG image, else the entity's primary content image, else null.
 * Manual `og_image_url` is returned untouched and never overwritten.
 * Categories have no automatic source.
 */
export const resolveEffectiveOgImage = (doc = {}, contentType = 'page') => {
  const manual = (doc.og_image_url || '').trim();
  if (manual) return manual;

  if (contentType === 'category') return null;

  if (contentType === 'listing') {
    for (const m of mediaImages(doc)) {
      const url = (m.public_url || '').trim();
      if (url) return url;
    }
    return null;
  }

  if (contentType === 'blog') {
    const cover = (doc.cover_image_url || '').trim();
    if (cover) return cover;
  } else {
    // 'page' — location pages carry a hero; static pages do not
    const hero = (doc.hero_image_url || '').trim();
    if (hero) return hero;
  }

  for (const img of bodyImages(bodyHtml(doc))) {
    if (img.url) return img.url;
  }
  return null;
};

/**
 * Display-only helper: make a possibly-relative media URL absolute so social
 * crawlers can fetch it. Not part of the backend parity surface.
 */
export const absoluteUrl = (url) => {
  if (!url) return undefined;
  if (/^https?:\/\//i.test(url) || url.startsWith('data:')) return url;
  return `${process.env.REACT_APP_BACKEND_URL || ''}${url}`;
};
